import { useMemo, useState } from "react";

import { type AssetType } from "@doitintl/cmp-models";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { globalText } from "../../../../../assets/texts";
import BucketNameField from "./BucketNameField";
import { getBucketTypeDisplayName } from "./utils";

type Props = {
  promptedAssetType: AssetType | null;
  availableBucketTypes: AssetType[];
  assetsMissingDefaultBucket: AssetType[];
  onClose: () => void;
  createBucket: ({ name, type, isDefault }: { name: string; type: AssetType; isDefault: boolean }) => void;
};

const BucketForm = ({
  promptedAssetType,
  availableBucketTypes,
  onClose,
  createBucket,
  assetsMissingDefaultBucket,
}: Props) => {
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(promptedAssetType ?? null);
  const [bucketName, setBucketName] = useState<string>("");
  const [bucketNameHasError, setBucketNameHasError] = useState<boolean>(false);
  const [isDefault, setIsDefault] = useState<boolean>(false);

  const isSubmitDisabled = useMemo(
    () => !selectedAssetType || !!bucketNameHasError || !bucketName.trim(),
    [selectedAssetType, bucketNameHasError, bucketName]
  );

  const isDefaultForced = useMemo(
    () => !!selectedAssetType && assetsMissingDefaultBucket.includes(selectedAssetType),
    [assetsMissingDefaultBucket, selectedAssetType]
  );

  const onSubmit = () => {
    if (!selectedAssetType) {
      return;
    }

    createBucket({ name: bucketName, type: selectedAssetType, isDefault: isDefault || isDefaultForced });
    onClose();
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle id="create-bucket-form-title">Create new invoice bucket</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <BucketNameField
            bucketName={bucketName}
            setBucketName={setBucketName}
            setBucketNameHasError={setBucketNameHasError}
          />

          <Autocomplete
            options={availableBucketTypes}
            value={selectedAssetType ?? undefined}
            disableClearable
            getOptionLabel={(option) => getBucketTypeDisplayName(option) ?? ""}
            renderInput={(params) => <TextField {...params} label="Select asset type" size="small" />}
            onChange={(_event, newValue) => {
              setSelectedAssetType(newValue as AssetType);
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={isDefault || isDefaultForced}
                onChange={() => {
                  setIsDefault(!isDefault);
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Set as default bucket for this asset type</Typography>
                <Typography variant="caption">Unassigned assets will be automatically added to this bucket.</Typography>
              </Box>
            }
            disabled={isDefaultForced}
          />
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="text" onClick={onClose} data-cy="bucket-form-cancel">
          {globalText.CANCEL}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={onSubmit}
          disabled={isSubmitDisabled}
          data-cy="bucket-form-submit"
        >
          Create bucket
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BucketForm;
